import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useSeo } from '@/lib/seo';
import { isContentSettled, useLiveTick } from '@/lib/liveContent';
import { getPulse, PULSE_DEMO_HREF, PULSE_PRICING_HREF, PULSE_ROUTE } from '@/data/pulse';
import { trackEvent } from '@/lib/analytics';
import { PulseHero } from '@/components/site/pulse/PulseHero';
import { PulseProblem } from '@/components/site/pulse/PulseProblem';
import { PulseWorkflow } from '@/components/site/pulse/PulseWorkflow';
import { PulseCapabilities } from '@/components/site/pulse/PulseCapabilities';
import { PulseTimesheets } from '@/components/site/pulse/PulseTimesheets';
import { PulseEmployees } from '@/components/site/pulse/PulseEmployees';
import { PulseCompliance } from '@/components/site/pulse/PulseCompliance';
import { PulseConnect } from '@/components/site/pulse/PulseConnect';
import { PulseProfitability } from '@/components/site/pulse/PulseProfitability';
import { PulseExperiences } from '@/components/site/pulse/PulseExperiences';
import { PulseIndustries } from '@/components/site/pulse/PulseIndustries';
import { PulseImplementation } from '@/components/site/pulse/PulseImplementation';
import { PulsePricing } from '@/components/site/pulse/PulsePricing';
import { PulseFaq } from '@/components/site/pulse/PulseFaq';
import { PulseRelated } from '@/components/site/pulse/PulseRelated';
import { CtaBand } from '@/components/site/CtaBand';

// Public Amden Pulse product-marketing page. All content reads from the
// CMS-backed listing record (cms_listings / amden-pulse); the Pulse
// application itself is separate and untouched.
export default function PulsePage() {
  useLiveTick();
  const listingsSettled = isContentSettled('listings');
  const listing = getPulse();
  const sp = listing?.systemPage;
  const ready = listing && listing.status === 'published' && sp;

  useSeo({
    title: listing?.seo?.title || 'Amden Pulse | Timesheets, Workforce & Site Profitability',
    description:
      listing?.seo?.description ||
      'Connect timesheets, employee records, leave, compliance, labour costs and site profitability with Amden Pulse.',
    canonical: listing?.seo?.canonical || `https://amden.ie${PULSE_ROUTE}`,
    ogImage: listing?.seo?.ogImage,
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://amden.ie/' },
            { '@type': 'ListItem', position: 2, name: 'Amden Pulse', item: `https://amden.ie${PULSE_ROUTE}` },
          ],
        },
        // Verified facts only — no offers, ratings or invented metrics.
        {
          '@type': 'SoftwareApplication',
          name: 'Amden Pulse',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          description: sp?.strapline,
          url: `https://amden.ie${PULSE_ROUTE}`,
          provider: { '@type': 'Organization', name: 'Amden Group', url: 'https://amden.ie' },
        },
      ],
    },
  });

  useEffect(() => {
    if (ready) trackEvent('pulse_page_viewed');
  }, [ready]);

  if (!ready) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 bg-white px-4 text-center" data-testid="pulse-loading">
        {!listingsSettled ? (
          <>
            <Loader2 className="h-6 w-6 animate-spin text-navy-900" aria-hidden="true" />
            <span className="sr-only">Loading Amden Pulse</span>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-extrabold tracking-tight text-navy-900">Amden Pulse</h1>
            <p className="max-w-md text-sm leading-relaxed text-slate-600">
              This page is currently being updated. To learn about Amden Pulse in the meantime, contact the Amden team.
            </p>
            <Link to="/contact" className="mt-2 rounded-md bg-navy-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-navy-700">
              Contact Amden Group
            </Link>
          </>
        )}
      </div>
    );
  }

  return (
    <>
      <PulseHero sp={sp} />
      <PulseProblem sp={sp} />
      <PulseWorkflow sp={sp} />
      <PulseCapabilities sp={sp} />
      <PulseTimesheets sp={sp} />
      <PulseEmployees sp={sp} />
      <PulseCompliance sp={sp} />
      <PulseConnect sp={sp} />
      <PulseProfitability sp={sp} />
      <PulseExperiences sp={sp} />
      <PulseIndustries sp={sp} />
      <PulseImplementation sp={sp} />
      <PulsePricing sp={sp} listing={listing} />
      <PulseFaq />
      <PulseRelated sp={sp} />
      <CtaBand
        index="15"
        eyebrow="Amden Pulse"
        heading="Ready to connect your workforce, costs and performance?"
        copy="See how Pulse can simplify everyday workforce management while giving you clearer visibility of sites, customers and profitability."
        primaryLabel="Book a Pulse Demonstration"
        primaryHref={PULSE_DEMO_HREF}
        secondaryLabel="Request Pricing"
        secondaryHref={PULSE_PRICING_HREF}
      />
    </>
  );
}
